import Container from "../../components/element/container";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import company from "../../content/policy.company.mjs";
import hero from "../../assets/images/desktop/consign-hero-v2.png";
import "./company.scss";
import { ShowroomGoogleMap, ShowroomWaze } from "../../assets/images";
import { phoneNumbers, socialLinks } from "../../content/contact";
import usePageReveal from "../../components/hook/use.page.reveal";

const address = company.address.join(" ");
const mapQuery = encodeURIComponent(address);
const directions = `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;

function PageHeading({ eyebrow, title, description }) {
  return (
    <header className="company-heading">
      <span className="company-eyebrow">{eyebrow}</span>
      <h1>{title}</h1>
      <p>{description}</p>
    </header>
  );
}

export function AboutUs() {
  const revealRef = usePageReveal();
  return (
    <section className="company-page" ref={revealRef}>
      <Container tag="div" className="company-inner">
        <section className="company-hero">
          <Container tag="div">
            <PageHeading
              eyebrow="ABOUT DCAR"
              title={
                <>
                  Your next chapter.
                  <br />
                  <em>Your next car.</em>
                </>
              }
              description="Buying or selling a car is a big decision. We’re here to make the journey easier, from your first enquiry to the handover."
            />
            <Container tag="div" className="company-actions">
              <Link className="company-button is-solid" to="/#home-product-list">
                Explore our cars <Icon icon="solar:arrow-right-linear" />
              </Link>
              <Link className="company-button secondary" to="/consign">
                Sell with DCAR
              </Link>
            </Container>
          </Container>
          <Container tag="div" className="company-hero-image">
            <img src={hero} alt="Luxury vehicle at DCAR" />
            <span>BUY. SELL. MOVE FORWARD.</span>
          </Container>
        </section>
        <section className="company-story">
          <span className="company-eyebrow">WHO WE ARE</span>
          <Container tag="div">
            <h2>Local roots. A passion for cars.</h2>
            <p>
              DCAR is operated by {company.legalName}, based in Klang, Selangor.
              We connect people looking for their next vehicle with owners ready
              to sell, with vehicle consignment and related automotive services
              in one place.
            </p>
            <p>
              Our mission is simple: help you buy or sell a car with less
              hassle, with support at each step of the process.
            </p>
          </Container>
        </section>
        <section aria-labelledby="company-services">
          <h2 id="company-services">A little support goes a long way.</h2>
          <Container tag="div" className="company-cards">
            {[
              [
                "solar:wheel-linear",
                "Find your next car",
                "Browse vehicle listings, review the details and get in touch to arrange your next step.",
                "/#home-product-list",
                "Browse cars",
              ],
              [
                "solar:key-linear",
                "Sell with support",
                "Start a consignment enquiry and let our team help with marketing, viewings and the sale process.",
                "/consign",
                "Explore consignment",
              ],
              [
                "solar:settings-linear",
                "Care beyond the sale",
                "Explore our panel workshops for vehicle inspections and related automotive services.",
                "/workshop",
                "Find a workshop",
              ],
            ].map(([icon, title, text, to, label]) => (
              <article className="company-card" key={title}>
                <Icon icon={icon} />
                <h3>{title}</h3>
                <p>{text}</p>
                <Link to={to}>
                  {label} <span aria-hidden="true">↗</span>
                </Link>
              </article>
            ))}
          </Container>
        </section>
        <ContactSection />
      </Container>
    </section>
  );
}

function ContactSection() {
  return (
    <section className="company-contact-section company-light" id="contact" aria-labelledby="contact-title">
      <span className="company-eyebrow">LET’S TALK</span>
      <h2 id="contact-title">Every journey starts with a conversation.</h2>
      <p>A car you’ve spotted, or a vehicle you’d like to sell? Get in touch with our team.</p>
      <Container tag="div" className="company-contact-grid">
        <Container tag="div" className="company-contact-details">
          <article className="company-contact-item">
            <Icon icon="solar:phone-calling-linear" />
            <Container tag="div"><h3>Give us a call</h3><p>Speak with the DCAR team.</p><Container tag="div" className="company-phone-numbers">{phoneNumbers.map(phone => <a key={phone.href} href={phone.href}>{phone.label}</a>)}</Container></Container>
          </article>
          <article className="company-contact-item">
            <Icon icon="solar:letter-linear" />
            <Container tag="div"><h3>Send us an email</h3><p>For enquiries and customer support.</p><a href={`mailto:${company.email}`}>{company.email}</a></Container>
          </article>
          <Container tag="div" className="company-social"><h3>Follow DCAR</h3><nav aria-label="DCAR social media">{socialLinks.map(social => <a key={social.label} href={social.href} aria-label={social.label} target="_blank" rel="noopener noreferrer"><Icon icon={social.icon} /></a>)}</nav></Container>
          <p className="company-visit-note">Planning a vehicle viewing? Contact us before your visit to arrange the details.</p>
        </Container>
        <Container tag="div" className="company-map">
          <iframe title="DCAR address in Bandar Bukit Raja, Klang" src={`https://www.google.com/maps?q=${mapQuery}&output=embed`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
          <Container tag="div">
            <span className="company-eyebrow">FIND US IN KLANG</span>
            <h3>{company.legalName}</h3>
            <address>{address}</address>
            <Container tag="div" className="company-directions">
              <a href={directions} target="_blank" rel="noopener noreferrer" aria-label="Open in Google Maps"><img src={ShowroomGoogleMap} alt="" /></a>
              <a href={`https://www.waze.com/ul?q=${mapQuery}&navigate=yes`} target="_blank" rel="noopener noreferrer" aria-label="Open in Waze"><img src={ShowroomWaze} alt="" /></a>
            </Container>
          </Container>
        </Container>
      </Container>
    </section>
  );
}
